// 🧭 Coach de negocio · Asesor financiero (cálculos puros, sin red).
// - Margen real: de cada venta, cuánto queda después de pagar la mercadería.
// - Número de tranquilidad (punto de equilibrio): lo que hay que vender al mes para
//   cubrir gastos del negocio + pagos de deudas.
// - De cada 100 que vendes (inspirado en "Profit First", Mike Michalowicz).
// - Plan para salir de deudas: bola de nieve (Dave Ramsey: la más chica primero, para
//   ganar ánimo) o avalancha (la de interés más alto primero, para pagar menos).

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Categorías del presupuesto que NO son gastos de operación: la compra de mercadería
// ya la cubre el margen, y los pagos de deudas se cuentan aparte (desde Mis deudas).
const ES_MERCADERIA = /compra de (productos|mercader)|insumos|inventario/i;
const ES_DEUDA = /deuda|tarjeta|tc |pr[eé]stamo|cr[eé]dito/i;

export function tipoGasto(categoria) {
  if (ES_MERCADERIA.test(categoria || '')) return 'mercaderia';
  if (ES_DEUDA.test(categoria || '')) return 'deuda';
  return 'operacion';
}

// Margen real de las ventas con costo registrado (0.62 = te quedan 62 de cada 100).
export function margenReal(ventas = [], libres = []) {
  let venta = 0; let costo = 0;
  ventas.forEach((v) => {
    const c = n(v.costo_unitario);
    if (c <= 0) return;
    venta += n(v.total); costo += c * n(v.cantidad || 1);
  });
  libres.forEach((i) => { if (n(i.costo) > 0) { venta += n(i.monto); costo += n(i.costo); } });
  if (venta <= 0) return null;
  return Math.max(0, Math.min(1, (venta - costo) / venta));
}

// Saldo total de una deuda en la moneda del negocio (si tiene parte en dólares y no hay
// tipo de cambio, esa parte queda fuera y se avisa).
export function saldoDeuda(d) {
  const usd = n(d.saldo_usd);
  const tc = n(d.tipo_cambio);
  return { total: n(d.saldo) + (usd > 0 && tc > 0 ? usd * tc : 0), faltaCambio: usd > 0 && !(tc > 0) };
}

// Pago mínimo del mes en la moneda del negocio (el mínimo en dólares se convierte).
export function minimoDeuda(d) {
  const usd = n(d.pago_minimo_usd); const tc = n(d.tipo_cambio);
  return n(d.pago_minimo) + (usd > 0 && tc > 0 ? usd * tc : 0);
}

// ¿A dónde se va el pago mínimo de una tarjeta este mes? Intereses + otros cargos; lo que
// queda es lo que de verdad baja la deuda. Si no queda nada, la deuda SUBE pagando el mínimo.
export function desgloseMinimo(d) {
  const saldo = saldoDeuda(d).total;
  const interes = saldo * (n(d.tasa_anual) / 100 / 12);
  const cargos = n(d.cargos_mes);
  const pago = minimoDeuda(d);
  return { pago, interes, cargos, baja: pago - interes - cargos, sinTasa: !(n(d.tasa_anual) > 0) };
}

// Gastos de operación del mes: el presupuesto si lo hay; si no, lo gastado.
export function gastosOperacion(presupuesto = {}, gastadoPorCategoria = {}) {
  const fuente = Object.values(presupuesto || {}).some((x) => n(x) > 0) ? presupuesto : gastadoPorCategoria;
  return Object.entries(fuente || {}).reduce((s, [c, m]) => (tipoGasto(c) === 'operacion' ? s + n(m) : s), 0);
}

// Pagos de deudas al mes: los mínimos registrados en Mis deudas; si no hay, lo
// presupuestado en categorías de deuda.
export function pagosDeudas(deudas = [], presupuesto = {}) {
  const conMinimo = deudas.filter((d) => minimoDeuda(d) > 0);
  if (conMinimo.length) return conMinimo.reduce((s, d) => s + minimoDeuda(d), 0);
  return Object.entries(presupuesto || {}).reduce((s, [c, m]) => (tipoGasto(c) === 'deuda' ? s + n(m) : s), 0);
}

// ¿Cuánto hay que VENDER al mes para cubrir todo? (los gastos y deudas se pagan con lo
// que queda después de la mercadería, por eso se divide entre el margen).
export function numeroTranquilidad({ operacion, deudas, margen, diasVenta = 26 }) {
  if (!(margen > 0)) return null;
  const salida = n(operacion) + n(deudas);
  const ventas = salida / margen;
  return { salida, ventas, porDia: diasVenta > 0 ? ventas / diasVenta : ventas };
}

// De cada 100 que vendes, a dónde va (con las ventas que esperas este mes).
export function deCada100({ margen, operacion, deudas, ventasMes }) {
  if (!(margen > 0) || !(ventasMes > 0)) return null;
  const mercaderia = (1 - margen) * 100;
  const op = Math.min(100 - mercaderia, (n(operacion) / ventasMes) * 100);
  const de = Math.min(100 - mercaderia - op, (n(deudas) / ventasMes) * 100);
  const tuyo = 100 - mercaderia - op - de;
  const r = (x) => Math.round(x * 10) / 10;
  return { mercaderia: r(mercaderia), operacion: r(op), deudas: r(de), tuyo: r(tuyo), falta: tuyo <= 0 };
}

// Plan para salir de deudas. Cada mes: todas pagan su mínimo y lo EXTRA va a la deuda
// que toca según el método; cuando una se termina, su mínimo se suma al extra.
// Sin tasa, se calcula sin intereses (y se avisa).
export function planDeudas(deudas = [], { extra = 0, metodo = 'bola' } = {}) {
  const lista = deudas.map((d) => ({
    id: d.id, nombre: d.nombre, saldo: saldoDeuda(d).total,
    minimo: Math.max(minimoDeuda(d), 0), tasa: n(d.tasa_anual) / 100 / 12, sinTasa: !(n(d.tasa_anual) > 0), cargos: n(d.cargos_mes),
  })).filter((d) => d.saldo > 0.5);
  if (!lista.length) return { orden: [], meses: 0, libreEn: 0, interes: 0, sinTasa: false, alcanza: true };
  const orden = [...lista].sort((a, b) => (metodo === 'avalancha' ? b.tasa - a.tasa || a.saldo - b.saldo : a.saldo - b.saldo));
  const pagoTotal = orden.reduce((s, d) => s + d.minimo, 0) + n(extra);
  let mes = 0; let interes = 0;
  const terminaEn = {};
  const serie = [{ mes: 0, total: Math.round(orden.reduce((x, d) => x + d.saldo, 0)) }]; // saldo total al cierre de cada mes
  while (orden.some((d) => d.saldo > 0.5) && mes < 240) {
    mes += 1;
    let disponible = pagoTotal;
    for (const d of orden) { if (d.saldo > 0.5) { const i = d.saldo * d.tasa + d.cargos; d.saldo += i; interes += i; } }
    for (const d of orden) { if (d.saldo > 0.5) { const p = Math.min(d.minimo, d.saldo, disponible); d.saldo -= p; disponible -= p; } }
    for (const d of orden) { if (disponible <= 0) break; if (d.saldo > 0.5) { const p = Math.min(d.saldo, disponible); d.saldo -= p; disponible -= p; } }
    for (const d of orden) { if (d.saldo <= 0.5 && !terminaEn[d.id || d.nombre]) terminaEn[d.id || d.nombre] = mes; }
    serie.push({ mes, total: Math.round(orden.reduce((x, d) => x + Math.max(0, d.saldo), 0)) });
    if (mes === 1 && pagoTotal <= 0) break;
  }
  const alcanza = !orden.some((d) => d.saldo > 0.5);
  return {
    orden: orden.map((d) => ({ id: d.id, nombre: d.nombre, terminaEn: terminaEn[d.id || d.nombre] || null })),
    meses: alcanza ? mes : null, libreEn: alcanza ? mes : null, interes: Math.round(interes),
    sinTasa: lista.some((d) => d.sinTasa), alcanza, pagoTotal, serie,
  };
}

// Pagos del mes agrupados por día de pago (ej. día 5: L 4,461 · día 15: L 3,593) y cuánto
// separar cada semana para no juntar todo el día del pago.
export function calendarioPagos(deudas = [], extra = 0) {
  const porDia = new Map();
  deudas.forEach((d) => {
    const m = minimoDeuda(d); if (!(m > 0)) return;
    const k = Number(d.dia_pago) || 0;
    const x = porDia.get(k) || { dia: k || null, total: 0, tarjetas: [] };
    x.total += m; x.tarjetas.push(d.nombre); porDia.set(k, x);
  });
  const fechas = [...porDia.values()].sort((a, b) => (a.dia || 99) - (b.dia || 99));
  const mensual = fechas.reduce((x, f) => x + f.total, 0) + n(extra);
  return { fechas, mensual, semanal: (mensual * 12) / 52 };
}

// Las categorías de deuda del presupuesto, con los mínimos reales de Mis deudas. Cada
// tarjeta va a la categoría que menciona su banco (1.ª palabra del nombre: "BAC", "Ficohsa");
// si no hay ninguna, se crea "Deuda <banco>". Devuelve el presupuesto nuevo.
export function presupuestoConDeudas(presupuesto = {}, deudas = []) {
  const nuevo = { ...(presupuesto || {}) };
  const catsDeuda = Object.keys(nuevo).filter((c) => tipoGasto(c) === 'deuda');
  const sumas = {};
  deudas.forEach((d) => {
    const m = minimoDeuda(d); if (!(m > 0)) return;
    const banco = String(d.nombre || '').trim().split(/\s+/)[0] || 'Deuda';
    const cat = catsDeuda.find((c) => c.toLowerCase().includes(banco.toLowerCase())) || `Deuda ${banco}`;
    sumas[cat] = (sumas[cat] || 0) + m;
  });
  if (!Object.keys(sumas).length) return nuevo;
  catsDeuda.forEach((c) => { if (!(c in sumas)) delete nuevo[c]; });
  Object.entries(sumas).forEach(([c, m]) => { nuevo[c] = Math.round(m); });
  return nuevo;
}

// Fecha aproximada (mes y año) en que quedas libre de deudas.
export function mesLibre(meses, hoy = new Date()) {
  if (!meses) return null;
  const d = new Date(hoy.getFullYear(), hoy.getMonth() + meses, 1);
  return d.toLocaleDateString('es-HN', { month: 'long', year: 'numeric' });
}
